<template>
  <div
    :class="[
      $style.container,
      {
        [$style.expire]: isExpiredStatus,
        [$style.success]: isSuccessStatus,
      },
    ]"
  >
    <div
      v-m-b="30"
      v-text-align:center
      :class="[
        $style.header,
        {
          [$style.expire]: isExpiredStatus,
          [$style.success]: isSuccessStatus,
        },
      ]"
    >
      <div
        v-m-b="4"
        :class="$style.title"
        v-html="displayTitle"
      ></div>

      <template v-if="!isSuccessStatus">
        <div :class="$style.date">
          <span
            v-text="$t('crypto.payment.process.expire', { date: displayExpiredAt })"
          ></span>
        </div>
        <div
          :class="$style.subtitle"
          v-text="displaySubtitle"
        ></div>
      </template>

      <template v-if="loading">
        <div
          :class="$style.subtitle"
          v-d-flex
          v-align="{ content: 'flex-start' }"
          v-justify="{ content: 'center' }"
          v-text="$t('crypto.payment.success.subtitle')"
        ></div>
      </template>
    </div>
    <div :class="$style.content">
      <template v-if="!isExpiredStatus">
        <div :class="$style.qrcode">
          <img :src="qrCode" alt="" />
        </div>
        <div
          :class="$style.hash"
          @click="handleCopy"
        >
          <div
            v-word-break:break-word
            v-text="transaction.hash"
          ></div>
          <base-icon
            v-m-l="10"
            v-color:violet
            name="basic_copy"
          />
        </div>
      </template>
      <div :class="$style.progress">
        <!-- TODO: доработать отображение текущей суммы полученных средств -->
        <base-progress
          v-m-b="4"
          :value="summary"
          :max="transaction.amount.amount"
        />
        <div :class="$style.range">
          <span
            v-opacity="isSuccessStatus ? 1 : .5"
            v-text="displaySummary"
          ></span>
          <span
            v-text="displayProcess"
          ></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatAmount } from 'lib/utils/amount';
import { copyToClipboard } from 'lib/utils/clipboard';
import { formatDateToCustomDate } from 'lib/utils/date';

import HTTPService from 'services/Http';

import BaseIcon from 'atoms/base/BaseIcon';
import BaseProgress from 'atoms/base/BaseProgress';

export default {
  name: 'CryptoPaymentInformation',
  components: {
    BaseIcon,
    BaseProgress,
  },
  props: {
    transaction: {
      type: Object,
      required: true,
    },
    loading: Boolean,
  },
  created() {
    if (this.isExpiredStatus) {
      return;
    }

    this.loadQrCode();
  },
  data() {
    return {
      qrCode: '',
    };
  },
  computed: {
    summary() {
      const { transactions } = this.transaction;

      if (this.$validate.isNotArray(transactions)) {
        return 0;
      }

      return transactions
        .reduce((acc, cur) => (acc += cur.amount), 0);
    },
    displayAmount() {
      return formatAmount(this.transaction.amount.amount, {
        significant_zeros: this.transaction.currency.significant_zeros,
        type: this.transaction.currency.type,
      });
    },
    displaySummary() {
      return formatAmount(this.summary, {
        significant_zeros: this.transaction.currency.significant_zeros,
        type: this.transaction.currency.type,
      });
    },
    displayTitle() {
      if (this.isExpiredStatus) {
        return this.$t('crypto.payment.expire.title');
      }

      if (this.isSuccessStatus) {
        return this.$t('crypto.payment.success.title');
      }

      return this.$t('crypto.payment.process.title', {
        amount: this.displayAmount,
        currency: this.transaction.currency.code,
      });
    },
    displaySubtitle() {
      if (this.isExpiredStatus) {
        return this.$t('crypto.payment.expire.subtitle');
      }

      return this.$t('crypto.payment.process.subtitle');
    },
    displayExpiredAt() {
      return formatDateToCustomDate(this.transaction.expiredAt, 'HH:mm D.MM.YYYY');
    },
    displayProcess() {
      return this.isSuccessStatus
        ? this.$t('crypto.payment.process.success')
        : `${this.displayAmount} ${this.transaction.currency.code}`;
    },
    isSuccessStatus() {
      return this.transaction.status === 'SUCCESS';
    },
    isExpiredStatus() {
      return this.transaction.status === 'EXPIRED';
    },
  },
  methods: {
    handleCopy() {
      copyToClipboard(this.transaction.hash);

      this.$alert({
        type: 'info',
        title: 'rejected.transaction.id.copied',
      });
    },
    async loadQrCode() {
      try {
        const { data, headers } = await HTTPService.getCryptoQrCodeByHash(this.transaction.hash);

        const blob = new Blob([data], {
          type: headers['content-type'],
        });

        this.qrCode = URL.createObjectURL(blob);
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style lang="sass" module>
  .container
    +font-type('regular', 12, 16)
    color: var(--color-blue-black)
    position: relative

    &:after
      content: ''
      width: 100vw
      height: calc(100% + 50px)
      position: absolute
      top: -30px
      left: 0
      margin-left: 50%
      background: linear-gradient(249.76deg, rgba(55, 181, 94, 0.2) -8.32%, rgba(123, 211, 255, 0.2) 108.85%)
      transform: translateX(-50%)
      z-index: -1

    &.expire
      &:after
        background: linear-gradient(249.76deg, rgba(235, 0, 27, 0.7) -8.32%, rgba(247, 158, 27, 0.7) 108.85%), rgba(255, 255, 255, 0.5)

      .subtitle
        +respond-to('medium')
          +justify-content(flex-end)

      .date

        +respond-to('medium')
          text-align: left

        span
          background-color: #FF5172

      .content
        grid-template-areas: "progress progress"

  .header
    display: grid
    grid-gap: 4px
    grid-template-areas: "title title" "date date" "subtitle subtitle"

    +respond-to('medium')
      grid-template-areas: "title title" "date subtitle"

    &.success
      grid-template-areas: "title title" "subtitle subtitle"

    &.expire
      grid-template-areas: "title title" "subtitle subtitle" "date date"

      +respond-to('medium')
        grid-template-areas: "title title" "subtitle date"

  .title
    +font-type('semi-bold', 24, 30)
    grid-area: title

  .subtitle
    grid-area: subtitle

    +respond-to('medium')
      +flexbox
      +align-items(center)

  .date
    +font-type('bold', 12, 16)
    grid-area: date

    +respond-to('medium')
      text-align: right

    span
      display: inline-block
      background-color: var(--color-white)
      padding: 10px

  .content
    position: relative
    display: grid
    grid-gap: 5px
    grid-template-areas: "qrcode qrcode" "hash hash" "progress progress"
    z-index: 1000

    +respond-to('medium')
      grid-template-areas: "qrcode hash" "qrcode progress"

  .qrcode
    +flexbox
    grid-area: qrcode
    border-radius: 5px
    background-color: var(--color-white)
    box-shadow: 0px 1px 2px rgba(189, 189, 189, 0.5)

    img
      width: 100%
      min-width: 145px

  .hash
    +flexbox
    +align-items(flex-start)
    +font-type('regular', 16, 24)
    color: var(--color-black-2)
    grid-area: hash
    padding: 14px 16px
    border-radius: 5px
    background-color: var(--color-white)
    box-shadow: 0px 1px 2px rgba(189, 189, 189, 0.5)
    cursor: pointer

  .progress
    +flexbox
    +flex-direction(column)
    +font-type('medium', 16, 24)
    color: var(--color-blue-black)
    grid-area: progress
    padding: 14px 16px
    border-radius: 5px
    background-color: var(--color-white)
    box-shadow: 0px 1px 2px rgba(189, 189, 189, 0.5)

  .range
    +flexbox
    +justify-content(space-between)
</style>
